/* eslint-disable react/no-unescaped-entities */
import { useEffect, useState } from "react";
import { useApp } from "../contexts/AppContext";
import { api } from "../lib/api";
import { postOrQueue } from "../lib/offline";
import { speak } from "../lib/i18n";
import { useRecorder, blobToB64 } from "../lib/recorder";
import { toast } from "sonner";
import { HeartHandshake, Smile, Meh, Frown, Mic, Square, Trash2, Volume2, Check, Loader2, Send } from "lucide-react";

const PENDING_KEY = "bc_followups_pending";
const DONE_KEY = "bc_followups_done";
export const ETATS = [
  { code: "mieux", Icon: Smile, fr: "Mieux", ln: "Malamu koleka", color: "#5CB85C" },
  { code: "pareil", Icon: Meh, fr: "Pareil", ln: "Ndenge moko", color: "#D4AF5A" },
  { code: "moins_bien", Icon: Frown, fr: "Moins bien", ln: "Mabe koleka", color: "#D9534F" },
];
const readJson = (k, d) => { try { return JSON.parse(localStorage.getItem(k) || "null") ?? d; } catch { return d; } };

export function FollowupCard() {
  const { patient, lang, online } = useApp();
  const [items, setItems] = useState(() => readJson(PENDING_KEY, []));
  const [done, setDone] = useState(() => readJson(DONE_KEY, []));
  const [etat, setEtat] = useState(null);
  const [busy, setBusy] = useState(false);
  const { rec, start, stop, reset, supported } = useRecorder(60);
  const ln = lang === "ln";

  useEffect(() => {
    if (!patient) return;
    api.get(`/patients/${patient.id}/worry-followups/pending`).then(({ data }) => { setItems(data.items); localStorage.setItem(PENDING_KEY, JSON.stringify(data.items)); }).catch(() => null);
  }, [patient, online]);

  const current = items.find((w) => !done.includes(`${w.id}:${w.etape}`));
  if (!patient || !current) return null;
  const prenom = (patient.nom || "").split(" ").pop();
  const j3 = current.etape === "j3";
  const question = j3
    ? (ln ? `Mikolo misato esili banda mitungisi. ${prenom} azali ndenge nini sikoyo ?` : `Il y a 3 jours vous avez signalé une inquiétude. Comment va ${prenom} maintenant ?`)
    : (ln ? `Lobi otindaki mitungisi. ${prenom} azali ndenge nini lelo ?` : `Hier vous avez signalé une inquiétude. Comment va ${prenom} aujourd'hui ?`);

  const send = async () => {
    if (!etat) { toast.error(ln ? "Pona eyano" : "Choisissez une réponse"); return; }
    setBusy(true);
    const body = { etat, etape: current.etape || "j1" };
    if (rec.blob) { body.audio_b64 = await blobToB64(rec.blob); body.mime = rec.mime || "audio/webm"; body.duree_s = rec.duree; }
    const r = await postOrQueue(`/alerts/worry/${current.id}/followup`, body);
    const nextDone = [...done, `${current.id}:${current.etape}`];
    setDone(nextDone); localStorage.setItem(DONE_KEY, JSON.stringify(nextDone));
    setEtat(null); reset();
    toast.success(r._queued ? (ln ? "Ekotindama soki réseau ezongi" : "Sera transmis au retour du réseau") : (ln ? "Praticien azwi nsango" : "Merci, votre praticien est informé"));
    setBusy(false);
  };

  return (
    <section data-testid="followup-card" className="rounded-3xl p-4 bg-[#0A1426] text-white flex flex-col gap-3">
      <div className="flex items-start gap-3">
        <span className="w-11 h-11 rounded-2xl bg-white/15 grid place-items-center shrink-0"><HeartHandshake size={22} className="text-[#D4AF5A]" /></span>
        <div className="flex-1">
          <div className="text-[11px] uppercase tracking-wider font-bold text-white/70 flex items-center gap-2">{ln ? "Suivi sima ya urgence" : "Suivi après urgence"} <span data-testid="followup-etape" className="px-2 py-0.5 rounded-full bg-[#D4AF5A] text-[#0A1426] text-[10px]">{j3 ? (ln ? "Mokolo 3" : "Jour 3") : "24 h"}</span></div>
          <div data-testid="followup-question" className="font-[Fraunces] text-lg font-bold leading-tight">{question}</div>
          <div className="text-[11px] text-white/70 mt-0.5">{new Date(current.date).toLocaleString("fr-FR", { dateStyle: "medium", timeStyle: "short" })} · {(current.signes || []).join(", ") || (ln ? "elembo te" : "aucun signe")}</div>
        </div>
        <button data-testid="followup-speak" onClick={() => speak(question, lang)} aria-label="Écouter" className="w-11 h-11 rounded-full bg-white/15 grid place-items-center"><Volume2 size={18} /></button>
      </div>
      <div className="grid grid-cols-3 gap-2" data-testid="followup-choices">
        {ETATS.map((e) => (
          <button key={e.code} data-testid={`followup-etat-${e.code}`} onClick={() => setEtat(e.code)}
            className={`h-24 rounded-2xl flex flex-col items-center justify-center gap-1 font-bold text-[13px] transition-transform active:scale-95 ${etat === e.code ? "ring-4 ring-white text-[#0A1426]" : "text-white bg-white/10"}`}
            style={etat === e.code ? { backgroundColor: e.color } : undefined}>
            <e.Icon size={34} style={etat === e.code ? undefined : { color: e.color }} /> {ln ? e.ln : e.fr}
          </button>
        ))}
      </div>
      {supported && (
        <div className="flex items-center gap-3 rounded-2xl bg-white/10 p-2.5">
          <button data-testid="followup-rec-btn" onClick={rec.state === "rec" ? stop : () => start().catch(() => toast.error("Micro indisponible"))} disabled={rec.state === "done" && !!rec.blob}
            className={`w-12 h-12 rounded-full grid place-items-center shrink-0 ${rec.state === "rec" ? "bg-[#D4AF5A] text-[#0A1426] animate-pulse" : "bg-white text-[#0A1426]"} disabled:opacity-50`}>
            {rec.state === "rec" ? <Square size={20} /> : <Mic size={22} />}
          </button>
          <div className="flex-1 text-[12px] text-white/85 leading-snug">
            {rec.state === "rec" ? `${rec.secs}s · ${ln ? "fina mpo na kosilisa" : "appuyez pour arrêter"}` : rec.blob ? `${ln ? "Ekomami" : "Enregistré"} · ${rec.duree}s` : (ln ? "Loba na mongongo (soki olingi), ekokomama" : "Expliquez de vive voix (facultatif), transcrit pour le praticien")}
          </div>
          {rec.blob && <button data-testid="followup-rec-delete" aria-label="Supprimer" onClick={reset} className="w-10 h-10 rounded-full bg-white/15 grid place-items-center"><Trash2 size={16} /></button>}
        </div>
      )}
      <button data-testid="followup-send" onClick={send} disabled={busy || !etat}
        className="h-14 rounded-2xl bg-[#D4AF5A] text-[#0A1426] font-bold text-[15px] flex items-center justify-center gap-2 disabled:opacity-50">
        {busy ? <Loader2 className="animate-spin" size={18} /> : <><Send size={18} /> {ln ? "Tinda na praticien" : "Envoyer au praticien"}</>}
      </button>
      {items.filter((w) => !done.includes(`${w.id}:${w.etape}`)).length > 1 && <div className="text-[11px] text-white/60 text-center"><Check size={10} className="inline" /> {items.filter((w) => !done.includes(`${w.id}:${w.etape}`)).length - 1} {ln ? "mosusu sima" : "autre(s) à suivre ensuite"}</div>}
    </section>
  );
}
